import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";

export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return online;
}

export function Logo({ size = "md", className = "" }) {
  const s = size === "lg" ? "text-2xl" : size === "sm" ? "text-sm" : "text-base";
  return (
    <div className={`flex items-center gap-2 font-head font-extrabold tracking-tight ${s} ${className}`}>
      <span className="grid place-items-center rounded-lg magenta-grad" style={{ width: 30, height: 30 }}>
        <ShieldCheck size={18} className="text-white" />
      </span>
      <span className="leading-none">
        CARNIVAL SAFETY <span style={{ color: "var(--cs-magenta)" }}>101</span>
        <sup className="text-[9px] align-super">™</sup>
      </span>
    </div>
  );
}

export function Page({ children, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const SEV = {
  info: { color: "var(--cs-blue)", label: "Info" },
  safety: { color: "var(--cs-amber)", label: "Safety Notice" },
  meeting: { color: "var(--cs-green)", label: "Meeting Point" },
  urgent: { color: "var(--cs-red)", label: "Urgent" },
};
export function SeverityDot({ severity }) {
  const s = SEV[severity] || SEV.info;
  return <span className="inline-block rounded-full" style={{ width: 10, height: 10, background: s.color }} />;
}
export function severityMeta(severity) { return SEV[severity] || SEV.info; }

const HELP_STATUS = {
  Requested: "var(--cs-amber)",
  Acknowledged: "var(--cs-blue)",
  "In Progress": "var(--cs-magenta)",
  Resolved: "var(--cs-green)",
};
export function StatusPill({ status }) {
  const color = HELP_STATUS[status] || "var(--cs-fg2)";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
      style={{ background: `${color}22`, color }}>
      <span className="rounded-full" style={{ width: 7, height: 7, background: color }} />
      {status}
    </span>
  );
}

export function Empty({ icon: Icon, title, sub }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center px-6">
      {Icon && <Icon size={40} className="mb-4" style={{ color: "var(--cs-fg2)" }} />}
      <p className="font-head font-bold text-lg">{title}</p>
      {sub && <p className="text-sm mt-1" style={{ color: "var(--cs-fg2)" }}>{sub}</p>}
    </div>
  );
}

export function Spinner({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
      <div className="rounded-full border-2 animate-spin"
        style={{ width: 34, height: 34, borderColor: "var(--cs-border)", borderTopColor: "var(--cs-magenta)" }} />
      {label && <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>{label}</p>}
    </div>
  );
}
